import React, { useEffect, useState } from "react";
import GuidedTour from "../../components/GuidedTour";
import { tourStepsMultipleChoice } from "./tourStepsMultipleChoice";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";

interface QuizQuestion {
  id: string;
  prompt: string;
  answers: string[];
  correctIndex: number;
}

interface ShuffledChoice {
  text: string;
  isCorrect: boolean;
}

interface AnswerColor {
  id: string;
  className: string;
}

const FALLBACK_ANSWER_COLORS: AnswerColor[] = [
  { id: "red", className: "bg-red-500 hover:bg-red-600 text-white" },
  { id: "blue", className: "bg-blue-500 hover:bg-blue-600 text-white" },
  { id: "yellow", className: "bg-yellow-400 hover:bg-yellow-500 text-black" },
  { id: "green", className: "bg-green-500 hover:bg-green-600 text-white" },
];

/** Named colors → button styles (matched from answer text when possible) */
const NAMED_ANSWER_COLORS: Record<string, AnswerColor> = {
  red: { id: "red", className: "bg-red-500 hover:bg-red-600 text-white" },
  blue: { id: "blue", className: "bg-blue-500 hover:bg-blue-600 text-white" },
  yellow: {
    id: "yellow",
    className: "bg-yellow-400 hover:bg-yellow-500 text-black",
  },
  green: { id: "green", className: "bg-green-500 hover:bg-green-600 text-white" },
  orange: {
    id: "orange",
    className: "bg-orange-500 hover:bg-orange-600 text-white",
  },
  purple: {
    id: "purple",
    className: "bg-purple-500 hover:bg-purple-600 text-white",
  },
  pink: { id: "pink", className: "bg-pink-400 hover:bg-pink-500 text-white" },
  black: { id: "black", className: "bg-black hover:bg-gray-900 text-white" },
  white: {
    id: "white",
    className: "bg-white hover:bg-gray-100 text-black",
  },
  gray: { id: "gray", className: "bg-gray-500 hover:bg-gray-600 text-white" },
  grey: { id: "gray", className: "bg-gray-500 hover:bg-gray-600 text-white" },
  brown: {
    id: "brown",
    className: "bg-amber-800 hover:bg-amber-900 text-white",
  },
  cyan: { id: "cyan", className: "bg-cyan-400 hover:bg-cyan-500 text-black" },
  teal: { id: "teal", className: "bg-teal-500 hover:bg-teal-600 text-white" },
  lime: { id: "lime", className: "bg-lime-400 hover:bg-lime-500 text-black" },
  indigo: {
    id: "indigo",
    className: "bg-indigo-500 hover:bg-indigo-600 text-white",
  },
  violet: {
    id: "violet",
    className: "bg-violet-500 hover:bg-violet-600 text-white",
  },
  gold: { id: "gold", className: "bg-yellow-500 hover:bg-yellow-600 text-black" },
  silver: {
    id: "silver",
    className: "bg-gray-300 hover:bg-gray-400 text-black",
  },
};

const STORAGE_KEY = "multipleChoiceQuestions";

const createId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `choice-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const normalizeColorKey = (text: string) =>
  text.trim().toLowerCase().replace(/[\s_-]+/g, "");

const matchNamedColor = (answerText: string): AnswerColor | null => {
  const key = normalizeColorKey(answerText);
  if (NAMED_ANSWER_COLORS[key]) {
    return NAMED_ANSWER_COLORS[key];
  }

  // Allow "sky blue", "dark green", etc. when the last word is a known color
  const words = answerText.trim().toLowerCase().split(/[\s_-]+/);
  const lastWord = words[words.length - 1];
  if (lastWord && NAMED_ANSWER_COLORS[lastWord]) {
    return NAMED_ANSWER_COLORS[lastWord];
  }

  return null;
};

/**
 * Prefer button colors that match the answer text (e.g. "Blue" → blue button).
 * Non-color answers use leftover palette colors.
 */
const getChoiceClassNames = (choices: ShuffledChoice[]): string[] => {
  const usedIds = new Set<string>();
  const classNames: (string | null)[] = choices.map(() => null);

  choices.forEach((choice, index) => {
    const matched = matchNamedColor(choice.text);
    if (matched) {
      classNames[index] = matched.className;
      usedIds.add(matched.id);
    }
  });

  let fallbackIndex = 0;
  choices.forEach((_, index) => {
    if (classNames[index]) {
      return;
    }

    let picked: AnswerColor | null = null;
    for (let attempt = 0; attempt < FALLBACK_ANSWER_COLORS.length; attempt += 1) {
      const candidate =
        FALLBACK_ANSWER_COLORS[
          (fallbackIndex + attempt) % FALLBACK_ANSWER_COLORS.length
        ];
      if (!usedIds.has(candidate.id)) {
        picked = candidate;
        fallbackIndex = fallbackIndex + attempt + 1;
        break;
      }
    }

    if (!picked) {
      picked =
        FALLBACK_ANSWER_COLORS[index % FALLBACK_ANSWER_COLORS.length];
    }

    classNames[index] = picked.className;
    usedIds.add(picked.id);
  });

  return classNames.map(
    (className, index) =>
      className ??
      FALLBACK_ANSWER_COLORS[index % FALLBACK_ANSWER_COLORS.length].className,
  );
};

const shuffleChoices = (question: QuizQuestion): ShuffledChoice[] => {
  const choices = question.answers.map((text, index) => ({
    text,
    isCorrect: index === question.correctIndex,
  }));

  for (let index = choices.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [choices[index], choices[swapIndex]] = [choices[swapIndex], choices[index]];
  }

  return choices;
};

const loadSavedQuestions = (): QuizQuestion[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QuizQuestion[]) : [];
  } catch {
    return [];
  }
};

const emptyAnswerDrafts = () => ["", "", "", ""];

const MultipleChoice: React.FC = () => {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState<QuizQuestion[]>(loadSavedQuestions);
  const [promptDraft, setPromptDraft] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState(emptyAnswerDrafts);
  const [correctDraftIndex, setCorrectDraftIndex] = useState(0);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null,
  );
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [shuffledChoices, setShuffledChoices] = useState<ShuffledChoice[]>([]);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState<number | null>(
    null,
  );
  const [score, setScore] = useState(0);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_multipleChoice");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(questions));
  }, [questions]);

  const filledAnswers = answerDrafts
    .map((answer, index) => ({ answer: answer.trim(), index }))
    .filter((entry) => entry.answer.length > 0);

  const canSaveQuestion =
    promptDraft.trim().length > 0 &&
    filledAnswers.length >= 2 &&
    filledAnswers.some((entry) => entry.index === correctDraftIndex);

  const resetDraftForm = () => {
    setPromptDraft("");
    setAnswerDrafts(emptyAnswerDrafts());
    setCorrectDraftIndex(0);
    setEditingQuestionId(null);
  };

  const startEditingQuestion = (question: QuizQuestion) => {
    const nextAnswers = emptyAnswerDrafts();
    question.answers.forEach((answer, index) => {
      if (index < nextAnswers.length) {
        nextAnswers[index] = answer;
      }
    });
    setPromptDraft(question.prompt);
    setAnswerDrafts(nextAnswers);
    setCorrectDraftIndex(question.correctIndex);
    setEditingQuestionId(question.id);
  };

  const saveQuestion = () => {
    if (!canSaveQuestion) {
      return;
    }

    const answers = filledAnswers.map((entry) => entry.answer);
    const correctIndex = filledAnswers.findIndex(
      (entry) => entry.index === correctDraftIndex,
    );

    if (editingQuestionId) {
      setQuestions((currentQuestions) =>
        currentQuestions.map((question) =>
          question.id === editingQuestionId
            ? {
                ...question,
                prompt: promptDraft.trim(),
                answers,
                correctIndex,
              }
            : question,
        ),
      );
    } else {
      setQuestions((currentQuestions) => [
        ...currentQuestions,
        {
          id: createId(),
          prompt: promptDraft.trim(),
          answers,
          correctIndex,
        },
      ]);
    }

    resetDraftForm();
  };

  const removeQuestion = (idToRemove: string) => {
    setQuestions((currentQuestions) =>
      currentQuestions.filter((question) => question.id !== idToRemove),
    );
    if (editingQuestionId === idToRemove) {
      resetDraftForm();
    }
  };

  const showQuestion = (index: number, questionList = questions) => {
    const question = questionList[index];
    if (!question) {
      return;
    }
    setQuestionIndex(index);
    setShuffledChoices(shuffleChoices(question));
    setSelectedChoiceIndex(null);
  };

  const startGame = () => {
    if (questions.length === 0) {
      return;
    }
    setScore(0);
    setIsGameCompleted(false);
    setIsGameStarted(true);
    showQuestion(0, questions);
  };

  const handleChoiceClick = (choiceIndex: number) => {
    if (selectedChoiceIndex !== null || isGameCompleted) {
      return;
    }

    setSelectedChoiceIndex(choiceIndex);
    if (shuffledChoices[choiceIndex]?.isCorrect) {
      setScore((currentScore) => currentScore + 1);
    }
  };

  const goToNextQuestion = () => {
    if (questionIndex + 1 >= questions.length) {
      setIsGameCompleted(true);
      return;
    }
    showQuestion(questionIndex + 1);
  };

  const goBackToSetup = () => {
    setIsGameStarted(false);
    setIsGameCompleted(false);
    setSelectedChoiceIndex(null);
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_multipleChoice", "true");
  };

  const handleBack = () => {
    navigate("/games");
  };

  const currentQuestion = questions[questionIndex];
  const choiceClassNames = getChoiceClassNames(shuffledChoices);

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            <button
              type="button"
              onClick={startGame}
              className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
            >
              Restart
            </button>
            <button
              type="button"
              onClick={goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              Back to Setup
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Multiple Choice</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex w-full max-w-2xl flex-col items-center">
          <p className="inherit mb-4 text-center">
            Write a question and two to four answers. Players click a colored
            button. If an answer is a color name (like Blue or Orange), that
            button uses that color.
          </p>

          <label htmlFor="questionPrompt" className="mb-2 font-semibold">
            Question
          </label>
          <input
            id="questionPrompt"
            type="text"
            value={promptDraft}
            onChange={(event) => setPromptDraft(event.target.value)}
            placeholder="What color is the sky?"
            className="mb-4 min-h-[44px] w-full rounded border border-black bg-white p-2 text-base text-black"
          />

          <p className="mb-2 font-semibold">Answers</p>
          <p className="mb-3 text-sm">
            Mark the correct one. Leave extra answers blank if you only need
            two or three.
          </p>
          <div className="mb-4 flex w-full flex-col gap-2">
            {answerDrafts.map((answer, index) => {
              const previewColor = matchNamedColor(answer);
              return (
                <label key={index} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="correctAnswer"
                    checked={correctDraftIndex === index}
                    onChange={() => setCorrectDraftIndex(index)}
                    aria-label={`Mark answer ${index + 1} as correct`}
                  />
                  <input
                    type="text"
                    value={answer}
                    onChange={(event) => {
                      const nextAnswers = [...answerDrafts];
                      nextAnswers[index] = event.target.value;
                      setAnswerDrafts(nextAnswers);
                    }}
                    placeholder={`Answer ${index + 1}`}
                    className="min-h-[44px] w-full rounded border border-black bg-white p-2 text-base text-black"
                  />
                  {previewColor && (
                    <span
                      className={`size-8 shrink-0 rounded border border-black ${previewColor.className}`}
                      title={`Button will be ${previewColor.id}`}
                      aria-hidden
                    />
                  )}
                </label>
              );
            })}
          </div>
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={saveQuestion}
              disabled={!canSaveQuestion}
              className={`min-h-[44px] rounded border border-black bg-blue-500 px-4 py-2 text-white ${
                canSaveQuestion
                  ? "hover:bg-blue-600"
                  : "cursor-not-allowed opacity-50"
              }`}
            >
              {editingQuestionId ? "Save Changes" : "Add Question"}
            </button>
            {editingQuestionId && (
              <button
                type="button"
                onClick={resetDraftForm}
                className="min-h-[44px] rounded border border-black bg-gray-200 px-4 py-2 text-black hover:bg-gray-300"
              >
                Cancel Edit
              </button>
            )}
          </div>

          {questions.length > 0 && (
            <div className="mb-6 w-full">
              <h2 className="inherit mb-2 text-xl font-semibold">
                Your questions
              </h2>
              <ul className="flex flex-col gap-2">
                {questions.map((question) => (
                  <li
                    key={question.id}
                    className={`flex flex-col gap-2 rounded px-3 py-2 text-black sm:flex-row sm:items-start sm:justify-between ${
                      editingQuestionId === question.id
                        ? "bg-blue-100 ring-2 ring-blue-400"
                        : "bg-gray-100"
                    }`}
                  >
                    <span>
                      {question.prompt}{" "}
                      <span className="text-sm text-gray-600">
                        ({question.answers[question.correctIndex]})
                      </span>
                    </span>
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => startEditingQuestion(question)}
                        className="min-h-[44px] rounded border border-black bg-white px-3 py-1 text-sm font-semibold hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => removeQuestion(question.id)}
                        className="min-h-[44px] rounded border border-black bg-white px-3 py-1 text-sm font-semibold text-red-600 hover:bg-red-50"
                        aria-label="Remove question"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            type="button"
            onClick={startGame}
            disabled={questions.length === 0}
            className={`start-button max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              questions.length === 0
                ? "cursor-not-allowed opacity-50"
                : "cursor-pointer"
            }`}
          >
            Start Game
          </button>
        </div>
      ) : (
        <div className="multiple-choice-game flex w-full max-w-3xl flex-col items-center">
          <p className="mb-2 text-lg font-semibold">
            Score: {score}/{questions.length}
          </p>
          {isGameCompleted ? (
            <p className="text-2xl font-semibold">
              You got {score} of {questions.length} correct.
            </p>
          ) : (
            currentQuestion && (
              <>
                <p className="inherit mb-2 text-sm">
                  Question {questionIndex + 1} of {questions.length}
                </p>
                <p className="mb-6 text-center text-2xl font-bold">
                  {currentQuestion.prompt}
                </p>
                <div className="mb-6 grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
                  {shuffledChoices.map((choice, index) => {
                    const colorClassName =
                      choiceClassNames[index] ??
                      FALLBACK_ANSWER_COLORS[0].className;
                    const hasAnswered = selectedChoiceIndex !== null;
                    const isSelected = selectedChoiceIndex === index;
                    let stateClass = "";
                    if (hasAnswered && choice.isCorrect) {
                      stateClass = "ring-4 ring-white";
                    } else if (hasAnswered && isSelected && !choice.isCorrect) {
                      stateClass = "opacity-60 ring-4 ring-black";
                    }

                    return (
                      <button
                        key={`${choice.text}-${index}`}
                        type="button"
                        onClick={() => handleChoiceClick(index)}
                        disabled={hasAnswered}
                        className={`min-h-24 rounded-lg border-2 border-black p-4 text-xl font-bold ${colorClassName} ${stateClass} ${
                          hasAnswered ? "cursor-default" : "cursor-pointer"
                        }`}
                      >
                        {choice.text}
                      </button>
                    );
                  })}
                </div>
                {selectedChoiceIndex !== null && (
                  <>
                    <p
                      className={`mb-4 text-xl font-semibold ${
                        shuffledChoices[selectedChoiceIndex]?.isCorrect
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {shuffledChoices[selectedChoiceIndex]?.isCorrect
                        ? "Correct!"
                        : "Not quite."}
                    </p>
                    <button
                      type="button"
                      onClick={goToNextQuestion}
                      className="min-h-[44px] rounded border border-black bg-green-500 px-6 py-2 text-lg font-bold uppercase text-white hover:bg-green-600"
                    >
                      {questionIndex + 1 >= questions.length
                        ? "See Score"
                        : "Next Question"}
                    </button>
                  </>
                )}
              </>
            )
          )}
        </div>
      )}

      <GuidedTour
        steps={tourStepsMultipleChoice()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="multipleChoice"
      />
    </div>
  );
};

export default MultipleChoice;
